import React, { useState, useEffect } from "react";
import { CancelButton, SaveButton, DrawerComponent, TableComponent, SkeletonComponent, trans } from "@comps/components";
import { getRolesPermissionReq, getRolePermissionsReq, storePermissionsReq } from "../requests";
import { Row, Col, Checkbox } from "antd";
import toast from "@comps/toast";
import { makeRequest } from "@utils/helpers";

/**
 * Last-resort label for a permission with no entry in
 * locales/en/permissions.json: "onholdcases_view" reads as "Onholdcases
 * view" rather than as the raw key, which is what the screen showed
 * before.
 */
const humanise = (name) => {
  const words = String(name || '').replace(/_/g, ' ').trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : '';
};

/*
 * Both opening requests still go through makeRequest — it is what signs a
 * user out on a 401 — but neither drives a loading flag of its own; the
 * pair is tracked as one wait below.
 */
const noLoader = () => {};

const UpdatePermissions = (props) => {
  /*
   * Loading and saving are two different things and they get two
   * different flags. Sharing one meant the Save button sat on
   * "Working…" for the whole of the opening fetch, as though a submit
   * the user never made were already in flight.
   *
   * `loading` starts true because the fetch below starts on mount: had it
   * started false, the drawer would paint one frame of "no permissions"
   * before the request it is waiting for even begins.
   */
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dataSource, setDataSource] = useState([]);
  const [allowPermissions, setAllowPermissions] = useState([]);

  useEffect(() => {
    getPermissionByRoleData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /*
   * Two requests, one wait. The role's own permissions decide which boxes
   * are ticked in the full permission tree, so the tree cannot be built
   * until both have answered — and the drawer must not settle in
   * between. Routing each through its own makeRequest did exactly that:
   * the first call set the loader back to false on its way out, the table
   * saw "not loading, no rows" and showed the empty state, and the list
   * then replaced it a moment later.
   */
  const getPermissionByRoleData = () => {
    setLoading(true);
    makeRequest(noLoader, getRolePermissionsReq, props.roleId, onPermissionSuccess, onFetchFailed);
  }

  const onPermissionSuccess = (data) => {
    setAllowPermissions(data);
    makeRequest(noLoader, getRolesPermissionReq, null, (all) => onSuccess(all, data), onFetchFailed);
  }

  const onFetchFailed = () => {
    toast.error("Permissions didn't load", "Close the drawer and try again.");
    setLoading(false);
  }

  const onSuccess = (data, currentRolePermissions) => {
    let finalData = updatePermissionsData(data, currentRolePermissions);
    setDataSource(finalData);
    setLoading(false);
  }

  const updatePermissionsData = (data, currentRolePermissions) => {
    let finalData = [];
    data.forEach((each) => {
      let obj = {};
      if (!each.children) {
        obj = {
          id: each.id,
          name: each.name,
          permissions: each.permissions.map((permission) => {
            let obj = {
              id: permission.id,
              name: permission.name,
              checked: checkCurrentPermissions(currentRolePermissions, permission),
            }
            return obj;
          }),
        }
      } else {
        obj = {
          id: each.id,
          name: each.name,
          permissions: each.permissions.map((permission) => {
            let obj = {
              id: permission.id,
              name: permission.name,
              checked: checkCurrentPermissions(currentRolePermissions, permission),
            }
            return obj;
          }),
          children: updatePermissionsData(each.children, currentRolePermissions)
        }
      }
      finalData.push(obj);
    });
    return finalData;
  }

  const checkCurrentPermissions = (currentPermissions, permission) => {
    let perm = currentPermissions.filter((each) => each.key === permission.name);
    if (perm.length > 0) {
      return true;
    }
    return false;
  }

  const onSavePermissions = () => {
    let payload = {
      role_id: props.roleId,
      permissions: allowPermissions
    }
    makeRequest(setSaving, storePermissionsReq, payload, onStorePermissionSuccess, Function);
  }

  const onStorePermissionSuccess = (data) => {
    toast.success("Permissions updated successfully");
  }

  const onChange = (permission, action) => {
    let obj = {
      id: permission.id,
      key: permission.name,
      permission_id: permission.id,
      role_id: props.roleId
    }
    if (action && allowPermissions.filter((each) => each.key === permission.name).length === 0) {
      let fdata = [...allowPermissions, obj];
      let fdatasource = updatePermissionsData(dataSource, fdata);
      setDataSource(fdatasource);
      setAllowPermissions(fdata);
    } else {
      let filtered = allowPermissions.filter((each) => each.key !== permission.name);
      let fdatasource = updatePermissionsData(dataSource, filtered);
      setDataSource(fdatasource);
      setAllowPermissions(filtered);
    }
  }

  const columns = [
    {
      title: "Module Name",
      dataIndex: "name",
      key: "name",
      width: "22%",
      render: (text, record) => {
        return (
          <strong>{ text }</strong>
        )
      }
    },
    {
      title: "Permissions",
      width: "78%",
      render: (record) => {
        return (
          // The grid used to sit in a wrapper pinned to 60% of a cell
          // that was itself 60% wide, so each checkbox had about a third
          // of the drawer to work with and any label longer than "Take
          // Case" ran straight over the one beside it. The cell is the
          // width now, and a long label wraps under its own box.
          <Row gutter={ [16, 8] }>
            {
              record.permissions.map((permission, index) => {
                return (
                  <Col span={ 12 } key={ index }>
                    <Checkbox
                      className="permission-check"
                      checked={ permission.checked }
                      onChange={ (event) => onChange(permission, event.target.checked) }
                    >{ trans('permissions.' + permission.name, humanise(permission.name)) }</Checkbox>
                  </Col>
                )
              })
            }
          </Row>
        )
      }

    },
  ];


  // ------------------------------------
  // Start footer buttons array
  // ------------------------------------
  // No Save while the matrix is still loading: there is nothing to save
  // yet, and a button that can only be pressed by mistake is worse than
  // no button. Cancel stays — a way out should always be there.
  const footer = (
    <div className="form-actions">
      <CancelButton key="close_button" onClick={ () => props.onClosed(false) } />
      { !loading &&
        <SaveButton key="save_button" onClick={ onSavePermissions } state={ saving } /> }
    </div>
  );
  // ------------------------------------
  // Eend footer buttons array
  // ------------------------------------

  return (
    <DrawerComponent width={ 1000 } mainTitle="Update" subTitle="Role Permissions" open={ true } footer={ footer } onClose={ () => props.onClosed(false) }>
      {/* A permission matrix only reads as a grid — never stack it into
          cards. It must not scroll sideways either: the default
          'max-content' width sized the table to the checkbox grid laid
          out in one unwrapped line, so opening Case Management (whose
          nested rows add indent and two more modules' worth of
          checkboxes) pushed the drawer into a horizontal scroll. Fixed
          layout honours the 22/78 split and lets the checkboxes wrap. */}
      { loading
        ? <SkeletonComponent variant="sections" rows={ 4 } />
        : <TableComponent
          cardsOnMobile={ false }
          scrollDisabled
          tableLayout="fixed"
          columns={ columns }
          dataSource={ dataSource }
          pagination={ false }
        /> }
    </DrawerComponent>
  );
}

export default UpdatePermissions
